import React, { useState } from 'react';
import axios from 'axios';
import {
  Building2,
  Star,
  Mic,
  HelpCircle,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Lock,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useAuth } from '../context/AuthContext';
import AdBlock from '../components/AdBlock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIP_CARDS = [
  {
    icon: <Building2 className="w-7 h-7" />,
    title: 'Research the Company',
    description: "Study the company's history, values, recent news, and competitors.",
    color: 'bg-blue-50 text-blue-600',
  },
  {
    icon: <Star className="w-7 h-7" />,
    title: 'STAR Method',
    description: 'Use Situation, Task, Action, Result framework for behavioural questions.',
    color: 'bg-amber-50 text-amber-600',
  },
  {
    icon: <Mic className="w-7 h-7" />,
    title: 'Practice Out Loud',
    description: 'Record yourself answering questions to improve delivery and confidence.',
    color: 'bg-rose-50 text-rose-600',
  },
  {
    icon: <HelpCircle className="w-7 h-7" />,
    title: 'Prepare Questions',
    description: 'Have 3-5 thoughtful questions ready to ask the interviewer.',
    color: 'bg-emerald-50 text-emerald-600',
  },
];

const COMMON_QUESTIONS = [
  {
    type: 'General',
    questions: [
      {
        q: 'Tell me about yourself.',
        a: "Start with your current role and key responsibilities, then briefly cover your background and what led you here. Finish with why you're excited about this opportunity. Keep it to about 2 minutes.",
        tips: ['Focus on professional highlights, not your life story.', 'Tailor your answer to align with the job description.'],
      },
      {
        q: 'Why do you want this job?',
        a: 'Explain what attracts you to the role — the work itself, the team, or the company mission. Show you have done your research and connect your skills to what they need.',
        tips: ["Mention something specific about the company you genuinely find compelling.", "Avoid saying 'the salary' as your primary reason."],
      },
      {
        q: 'What are your strengths and weaknesses?',
        a: 'For strengths, give a concrete example that demonstrates the quality in action. For weaknesses, choose something real but show the steps you are actively taking to improve.',
        tips: ['Never say "I work too hard" — it sounds rehearsed.', 'Frame weaknesses as areas of development, not fatal flaws.'],
      },
    ],
  },
  {
    type: 'Behavioural',
    questions: [
      {
        q: 'Tell me about a challenge you overcame.',
        a: "Use the STAR method: describe the situation, your specific task, the actions you took, and the measurable result. Focus on what you personally did rather than what the team did.",
        tips: ['Choose a real, meaningful challenge — not a minor inconvenience.', 'Emphasise what you learned from the experience.'],
      },
      {
        q: 'Describe a time you worked in a team.',
        a: 'Pick an example that shows collaboration, communication, and your specific contribution. Highlight how the team dynamic led to a successful outcome.',
        tips: ['Be specific about your own role within the team.', "Acknowledge others' contributions — it shows self-awareness."],
      },
    ],
  },
  {
    type: 'Career',
    questions: [
      {
        q: 'Where do you see yourself in 5 years?',
        a: "Show ambition balanced with realism. Describe growing within your area of expertise or taking on more responsibility. Tie your goals to what this company can offer.",
        tips: ['Show you are committed, not just passing through.', 'Avoid vague answers like "in a management role" without context.'],
      },
      {
        q: 'Why are you leaving your current role?',
        a: 'Be honest but constructive. Focus on what you are moving towards — new challenges, growth, a better fit — rather than what you are running away from.',
        tips: ['Never badmouth your current employer.', "Frame it positively: you're seeking growth, not escaping problems."],
      },
    ],
  },
];

function GeneratedQuestionCard({ item, index }) {
  const [showAnswer, setShowAnswer] = useState(false);

  return (
    <Card className="bg-white border border-gray-100 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3 flex-1">
            <span className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
              {index + 1}
            </span>
            <p className="font-semibold text-gray-900 text-base leading-snug">{item.question}</p>
          </div>
          {item.category && (
            <Badge className="bg-violet-100 text-violet-700 border-violet-200 shrink-0 text-xs">
              {item.category}
            </Badge>
          )}
        </div>

        <div className="ml-10">
          <button
            onClick={() => setShowAnswer(v => !v)}
            className="flex items-center gap-2 text-sm text-violet-700 font-medium hover:text-violet-900 transition-colors"
          >
            {showAnswer ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showAnswer ? 'Hide Sample Answer' : 'Show Sample Answer'}
          </button>

          {showAnswer && (
            <div className="mt-3 space-y-3">
              {item.sample_answer && (
                <div className="bg-violet-50 rounded-lg p-4 text-sm text-gray-700 leading-relaxed">
                  {item.sample_answer}
                </div>
              )}
              {item.tips && item.tips.length > 0 && (
                <ul className="space-y-1.5">
                  {item.tips.map((tip, ti) => (
                    <li key={ti} className="flex items-start gap-2 text-sm text-gray-600">
                      <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      {tip}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function InterviewPrepPage() {
  const { isAuthenticated, token } = useAuth();

  // AI generator state
  const [jobRole, setJobRole] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [count, setCount] = useState('5');
  const [loading, setLoading] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!jobRole.trim()) return;
    setLoading(true);
    setError('');
    setGeneratedQuestions([]);
    try {
      const res = await axios.post(
        `${API_URL}/api/ai/interview-practice`,
        { job_title: jobRole, job_description: jobDescription, count: parseInt(count, 10) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedQuestions(res.data?.questions || res.data || []);
    } catch (err) {
      setError('Failed to generate questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Hero */}
      <div className="bg-gradient-to-r from-violet-700 to-purple-700 text-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Ace Your Interview</h1>
          <p className="text-lg text-purple-200 max-w-2xl mx-auto">
            Expert tips, AI-powered practice questions, and a library of model answers to help you walk in confident.
          </p>
        </div>
      </div>

      {/* Section 1: Tip Cards */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Preparation Tips</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIP_CARDS.map((tip, i) => (
            <Card key={i} className="bg-white hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${tip.color}`}>
                  {tip.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{tip.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Ad Block between sections */}
      <AdBlock position="interview-prep" />

      {/* Section 2: AI Practice Questions Generator */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        <Card className="bg-white shadow-sm">
          <CardHeader className="border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Generate Practice Questions</CardTitle>
                <p className="text-sm text-gray-500 mt-0.5">AI-powered questions tailored to your target role</p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6">
            {!isAuthenticated ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-7 h-7 text-gray-400" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Sign in to generate AI-powered practice questions</h3>
                <p className="text-sm text-gray-500 mb-5">
                  Create a free account or sign in to access personalised interview preparation.
                </p>
                <div className="flex gap-3 justify-center">
                  <a href="/login">
                    <Button variant="outline">Sign In</Button>
                  </a>
                  <a href="/register">
                    <Button className="bg-[#003366] hover:bg-[#003366]/90 text-white">Get Started Free</Button>
                  </a>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Job Role <span className="text-red-500">*</span>
                    </label>
                    <Input
                      placeholder="e.g. Software Engineer, Marketing Manager"
                      value={jobRole}
                      onChange={e => setJobRole(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Number of Questions
                    </label>
                    <select
                      value={count}
                      onChange={e => setCount(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                      <option value="5">5 questions</option>
                      <option value="8">8 questions</option>
                      <option value="10">10 questions</option>
                    </select>
                  </div>
                </div>
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Job Description <span className="text-gray-400">(optional)</span>
                  </label>
                  <Textarea
                    placeholder="Paste the job description here for more targeted questions..."
                    value={jobDescription}
                    onChange={e => setJobDescription(e.target.value)}
                    rows={4}
                  />
                </div>

                {error && (
                  <p className="text-red-600 text-sm mb-4">{error}</p>
                )}

                <Button
                  onClick={handleGenerate}
                  disabled={loading || !jobRole.trim()}
                  className="bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate Questions
                    </>
                  )}
                </Button>

                {/* Generated Questions */}
                {generatedQuestions.length > 0 && (
                  <div className="mt-8 space-y-4">
                    <h3 className="font-semibold text-gray-900 text-lg">
                      Practice Questions for: <span className="text-violet-700">{jobRole}</span>
                    </h3>
                    {generatedQuestions.map((item, i) => (
                      <GeneratedQuestionCard key={i} item={item} index={i} />
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section 3: Common Questions Library */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Common Questions Library</h2>
        <div className="space-y-6">
          {COMMON_QUESTIONS.map((section, si) => (
            <Card key={si} className="bg-white shadow-sm overflow-hidden">
              <CardHeader className="bg-gray-50 border-b py-3 px-5">
                <CardTitle className="text-base font-semibold text-gray-700">{section.type} Questions</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Accordion type="single" collapsible className="w-full">
                  {section.questions.map((item, qi) => (
                    <AccordionItem key={qi} value={`${si}-${qi}`} className="border-b last:border-b-0 px-5">
                      <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-violet-700 text-sm py-4">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pb-4 space-y-3">
                          <div className="bg-violet-50 rounded-lg p-4 text-sm text-gray-700 leading-relaxed">
                            {item.a}
                          </div>
                          <ul className="space-y-1.5">
                            {item.tips.map((tip, ti) => (
                              <li key={ti} className="flex items-start gap-2 text-sm text-gray-600">
                                <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
